import { clsx, type ClassValue } from 'clsx';
import { LucideProps } from 'lucide-react';
import { ForwardRefExoticComponent, RefAttributes } from 'react';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export type LucideIconType = ForwardRefExoticComponent<
  Omit<LucideProps, 'ref'> & RefAttributes<SVGSVGElement>
>;

// Common color name mappings
const COLOR_NAMES: Record<string, string> = {
  '#000000': 'Black',
  '#ffffff': 'White',
  '#ff0000': 'Red',
  '#00ff00': 'Green',
  '#0000ff': 'Blue',
  '#ffff00': 'Yellow',
  '#ff00ff': 'Magenta',
  '#00ffff': 'Cyan',
  '#808080': 'Gray',
  '#c0c0c0': 'Silver',
  '#800000': 'Maroon',
  '#808000': 'Olive',
  '#008000': 'Dark Green',
  '#800080': 'Purple',
  '#008080': 'Teal',
  '#000080': 'Navy',
  '#ffa500': 'Orange',
  '#ffc0cb': 'Pink',
  '#ffb6c1': 'Light Pink',
  '#ff69b4': 'Hot Pink',
  '#a52a2a': 'Brown',
  '#8b4513': 'Saddle Brown',
  '#d2691e': 'Chocolate',
  '#f5f5dc': 'Beige',
  '#fffff0': 'Ivory',
  '#f0e68c': 'Khaki',
  '#e6e6fa': 'Lavender',
  '#add8e6': 'Light Blue',
  '#87ceeb': 'Sky Blue',
  '#4169e1': 'Royal Blue',
  '#1e3a5f': 'Dark Blue',
  '#6366f1': 'Indigo',
  '#8b5cf6': 'Violet',
  '#ec4899': 'Pink',
  '#f97316': 'Orange',
  '#22c55e': 'Green',
  '#ef4444': 'Red',
  '#800020': 'Burgundy',
};

/**
 * Converts a hex color code to a human-readable color name.
 * Returns the hex code if no match is found.
 */
export function getColorName(hex: string): string {
  const normalizedHex = hex.toLowerCase();
  return COLOR_NAMES[normalizedHex] || hex;
}

/**
 * Returns stock status label based on available quantity.
 */
export function getStockStatus(stock: number): {
  label: string;
  variant: 'success' | 'warning' | 'error';
} {
  if (stock <= 0) {
    return { label: 'Out of Stock', variant: 'error' };
  }
  if (stock <= 5) {
    return { label: 'Low Stock', variant: 'warning' };
  }
  return { label: 'In Stock', variant: 'success' };
}

/**
 * Resolves image URLs to the correct format.
 * Handles:
 * - Relative URLs (e.g., /public/image.png)
 * - Malformed localhost URLs (e.g., http://localhost/public/image.png)
 * - Absolute URLs (returned as-is)
 *
 * @param url - The image URL to resolve
 * @returns The resolved image URL
 */
export function resolveImageUrl(url: string): string {
  if (!url) return '';

  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  const r2PublicUrl = import.meta.env.VITE_R2_PUBLIC_URL;
  if (r2PublicUrl) {
    const cleanPath = url.replace(/^\/?public\//, '');
    return `${r2PublicUrl}/${cleanPath.startsWith('/') ? cleanPath.substring(1) : cleanPath}`;
  }

  const apiBase = import.meta.env.VITE_API_URL || '';
  const baseUrl = apiBase.replace(/\/api$/, '');

  if (url.includes('localhost/public/') || url.includes('127.0.0.1/public/')) {
    const match = url.match(/(?:localhost|127\.0\.0\.1)\/public\/(.+)/);
    if (match) {
      return `${baseUrl}/public/${match[1]}`;
    }
  }

  if (url.startsWith('/public/')) {
    return `${baseUrl}${url}`;
  }

  if (url.startsWith('/')) {
    return `${baseUrl}${url}`;
  }

  return url;
}
